import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import type { RechnungsStatus, RechnungsTyp } from '../lib/api-enums';
import {
  RECHNUNGS_TYP_LABELS as TYP_LABELS,
  RECHNUNGS_STATUS_LABELS as STATUS_LABELS,
  RECHNUNGS_STATUS_BADGE as STATUS_COLORS,
} from '../lib/api-labels';
import { formatGermanDateFromTimestamp } from '../lib/date-format';

interface Rechnung {
  id: string;
  rechnungsnummer: string | null;
  typ: RechnungsTyp;
  status: RechnungsStatus;
  kundeId: string;
  objektId: string | null;
  angebotId: string | null;
  originalRechnungId: string | null;
  titel: string;
  rechnungsdatum: string | null;
  zahlungszielTage: number;
  zahlungsart: string;
  summeNetto: string;
  summeUst: string;
  summeBrutto: string;
  bezahltAm: string | null;
  mahnstufe: number;
  versendetAm: string | null;
  storniertAm: string | null;
  createdAt: string;
}

interface PositionRow {
  id: string;
  positionNr: number;
  bezeichnung: string;
  beschreibung: string | null;
  menge: string;
  einheit: string;
  einzelpreisNetto: string;
  ustSatz: string;
  rabattProzent: string;
  positionNetto: string | null;
}

interface Kunde {
  id: string;
  kundennummer: string;
  firmenname: string;
}

export const Route = createFileRoute('/rechnungen/$id')({
  beforeLoad: () => requireAccess('/api/v1/rechnungen'),
  component: RechnungDetailPage,
});

function RechnungDetailPage() {
  const { id } = Route.useParams();
  const router = useRouter();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['rechnung', id],
    queryFn: async () => {
      try {
        return await api<{ item: Rechnung; positionen: PositionRow[] }>(`/api/v1/rechnungen/${id}`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
  const kundeQ = useQuery({
    queryKey: ['kunde', q.data?.item.kundeId],
    queryFn: () => api<{ item: Kunde }>(`/api/v1/kunden/${q.data!.item.kundeId}`),
    enabled: !!q.data?.item.kundeId,
  });

  const [actionError, setActionError] = useState<unknown>(null);
  const [bezahltAm, setBezahltAm] = useState(new Date().toISOString().slice(0, 10));

  const send = useMutation({
    mutationFn: () => api(`/api/v1/rechnungen/${id}/senden`, { method: 'POST' }),
    onSuccess: () => {
      setActionError(null);
      void qc.invalidateQueries({ queryKey: ['rechnung', id] });
      void qc.invalidateQueries({ queryKey: ['rechnungen'] });
    },
    onError: (err) => setActionError(err),
  });
  const pay = useMutation({
    mutationFn: () =>
      api(`/api/v1/rechnungen/${id}/bezahlt`, {
        method: 'POST',
        body: { bezahltAm },
      }),
    onSuccess: () => {
      setActionError(null);
      void qc.invalidateQueries({ queryKey: ['rechnung', id] });
      void qc.invalidateQueries({ queryKey: ['rechnungen'] });
    },
    onError: (err) => setActionError(err),
  });
  const storno = useMutation({
    mutationFn: () =>
      api<{ item: { id: string } }>(`/api/v1/rechnungen/${id}/storno`, { method: 'POST' }),
    onSuccess: async (res) => {
      setActionError(null);
      await qc.invalidateQueries({ queryKey: ['rechnungen'] });
      await router.navigate({
        to: '/rechnungen/$id',
        params: { id: res.item.id },
      });
    },
    onError: (err) => setActionError(err),
  });
  const del = useMutation({
    mutationFn: () => api(`/api/v1/rechnungen/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rechnungen'] });
      void router.navigate({ to: '/rechnungen' });
    },
    onError: (err) => setActionError(err),
  });

  if (q.isLoading) return <div className="text-slate-500">Lade…</div>;
  if (q.error) return <ErrorBanner error={q.error} />;
  if (!q.data) {
    return (
      <div className="space-y-4">
        <Link to="/rechnungen" className="text-sm text-brand-700 hover:underline">
          ← zurück
        </Link>
        <div className="rounded-md border border-slate-200 bg-white p-6 text-slate-500">
          Rechnung nicht gefunden.
        </div>
      </div>
    );
  }

  const r = q.data.item;
  const positionen = q.data.positionen;
  const kunde = kundeQ.data?.item ?? null;
  const canSend = r.status === 'entwurf' && positionen.length > 0;
  const canPay = ['versendet', 'teilweise_bezahlt', 'ueberfaellig'].includes(r.status);
  const canStorno = !['entwurf', 'storniert'].includes(r.status) && r.typ !== 'storno_gutschrift';
  const canDelete = r.status === 'entwurf';

  return (
    <div className="space-y-6">
      <div>
        <Link to="/rechnungen" className="text-sm text-brand-700 hover:underline">
          ← zurück zur Rechnungsliste
        </Link>
      </div>

      <PageHeader
        title={r.titel}
        description={`${TYP_LABELS[r.typ]} · ${r.rechnungsnummer ?? 'Entwurf'} · ${STATUS_LABELS[r.status]}`}
        actions={
          <div className="flex items-center gap-2">
            {canSend && (
              <Button onClick={() => send.mutate()} disabled={send.isPending}>
                Versenden
              </Button>
            )}
            {canPay && (
              <>
                <Input
                  type="date"
                  value={bezahltAm}
                  onChange={(e) => setBezahltAm(e.target.value)}
                  className="w-36"
                />
                <Button onClick={() => pay.mutate()} disabled={pay.isPending}>
                  Als bezahlt markieren
                </Button>
              </>
            )}
            {canStorno && (
              <Button
                variant="danger"
                onClick={() => {
                  if (confirm('Rechnung via Storno-Gutschrift stornieren?')) storno.mutate();
                }}
                disabled={storno.isPending}
              >
                Stornieren
              </Button>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                onClick={() => {
                  if (confirm('Entwurf wirklich löschen?')) del.mutate();
                }}
              >
                Löschen
              </Button>
            )}
          </div>
        }
      />

      <ErrorBanner error={actionError} />

      <Panel title="Stammdaten">
        <dl className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
          <Field label="Status">
            <span
              className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[r.status]}`}
            >
              {STATUS_LABELS[r.status]}
            </span>
          </Field>
          <Field label="Typ">{TYP_LABELS[r.typ]}</Field>
          <Field label="Rechnungsnummer">
            <span className="font-mono text-slate-600">
              {r.rechnungsnummer ?? <span className="italic text-slate-400">noch keine</span>}
            </span>
          </Field>
          <Field label="Kunde">
            {kunde ? (
              <Link
                to="/kunden/$id"
                params={{ id: kunde.id }}
                className="text-brand-700 hover:underline"
              >
                {kunde.kundennummer} — {kunde.firmenname}
              </Link>
            ) : (
              <span className="text-slate-400">Lade…</span>
            )}
          </Field>
          <Field label="Rechnungsdatum">{r.rechnungsdatum ?? '–'}</Field>
          <Field label="Zahlungsziel">
            {r.zahlungszielTage} Tage ({r.zahlungsart})
          </Field>
          <Field label="Versendet">
            {r.versendetAm ? formatGermanDateFromTimestamp(r.versendetAm) : '–'}
          </Field>
          <Field label="Bezahlt am">{r.bezahltAm ?? '–'}</Field>
          <Field label="Mahnstufe">{r.mahnstufe > 0 ? `Stufe ${r.mahnstufe}` : '–'}</Field>
          {r.originalRechnungId && (
            <div className="md:col-span-3">
              <Field label="Original (Storno-Referenz)">
                <Link
                  to="/rechnungen/$id"
                  params={{ id: r.originalRechnungId }}
                  className="text-brand-700 hover:underline font-mono text-xs"
                >
                  {r.originalRechnungId}
                </Link>
              </Field>
            </div>
          )}
          {r.angebotId && (
            <div className="md:col-span-3">
              <Field label="Aus Angebot">
                <Link
                  to="/angebote/$id"
                  params={{ id: r.angebotId }}
                  className="text-brand-700 hover:underline"
                >
                  → zur Angebots-Quelle
                </Link>
              </Field>
            </div>
          )}
        </dl>
      </Panel>

      <Panel title={`Positionen (${positionen.length})`}>
        {positionen.length === 0 ? (
          <div className="text-slate-400 text-sm">Keine Positionen.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr className="border-b border-slate-100">
                <th className="py-1.5 text-left w-8">#</th>
                <th className="py-1.5 text-left">Bezeichnung</th>
                <th className="py-1.5 text-right w-20">Menge</th>
                <th className="py-1.5 text-left w-16">Einheit</th>
                <th className="py-1.5 text-right w-24">Einzelpreis</th>
                <th className="py-1.5 text-right w-16">USt %</th>
                <th className="py-1.5 text-right w-16">Rabatt</th>
                <th className="py-1.5 text-right w-28">Netto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {positionen.map((p) => (
                <tr key={p.id} className="text-slate-800">
                  <td className="py-1.5 text-xs text-slate-400">{p.positionNr}</td>
                  <td className="py-1.5">
                    <div className="font-medium">{p.bezeichnung}</div>
                    {p.beschreibung && (
                      <div className="text-xs text-slate-500">{p.beschreibung}</div>
                    )}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{formatDe(p.menge, 3)}</td>
                  <td className="py-1.5 text-slate-600">{p.einheit}</td>
                  <td className="py-1.5 text-right tabular-nums">
                    {formatDe(p.einzelpreisNetto, 2)} €
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-slate-600">{p.ustSatz}</td>
                  <td className="py-1.5 text-right tabular-nums text-slate-600">
                    {Number(p.rabattProzent) > 0 ? `${p.rabattProzent}%` : '–'}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {p.positionNetto ? formatDe(p.positionNetto, 2) : '–'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200 text-sm font-medium">
                <td colSpan={7} className="py-1.5 text-right text-slate-600">
                  Summe netto
                </td>
                <td className="py-1.5 text-right tabular-nums">{formatDe(r.summeNetto, 2)} €</td>
              </tr>
              <tr className="text-sm text-slate-600">
                <td colSpan={7} className="py-1 text-right">
                  USt
                </td>
                <td className="py-1 text-right tabular-nums">{formatDe(r.summeUst, 2)} €</td>
              </tr>
              <tr className="text-sm font-semibold">
                <td colSpan={7} className="py-1 text-right">
                  Brutto
                </td>
                <td className="py-1 text-right tabular-nums">{formatDe(r.summeBrutto, 2)} €</td>
              </tr>
            </tfoot>
          </table>
        )}
      </Panel>
    </div>
  );
}

function Panel({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {actions}
      </header>
      <div className="p-4 space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

function formatDe(value: string, digits: number): string {
  return Number(value).toLocaleString('de-DE', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}
